import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import { Plus, Pencil, Trash2, Search, Briefcase, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../../../api/client';
import Pagination from '../../component/Pagination';
import NotificationPopup from '../../component/NotificationPopup';
import ConfirmationDialog from '../../../common/ConfirmationDialog';
import QuestionBankFormModal from './QuestionBankFormModal';
import {
  emptyQuestionForm,
  formFromBankItem,
  getQuestionTypeColor,
  getQuestionTypeOptions,
  getMcqChoices,
  isQuestionFormValid,
  payloadFromForm,
} from './questionBankUtils';

const getJdStatusStyle = (status) => {
  const styles = {
    open: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-red-100 text-red-700',
    expired: 'bg-amber-100 text-amber-800',
    archived: 'bg-slate-100 text-slate-600',
  };
  return styles[status] || 'bg-gray-100 text-gray-700';
};

export default function QuestionBank() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const questionTypeOptions = getQuestionTypeOptions(t);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [questionType, setQuestionType] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_count: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [form, setForm] = useState(emptyQuestionForm());
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [usageQuestion, setUsageQuestion] = useState(null);
  const [usageList, setUsageList] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (error) {
      return {};
    }
  }, []);
  const isSuperAdmin = userDetails?.is_superuser === true;
  const isCompanyAdmin = Array.isArray(userDetails?.admin_companies) && userDetails.admin_companies.length > 0;

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search.trim()) params.search = search.trim();
      if (questionType) params.question_type = questionType;
      if (selectedCompany) params.company = selectedCompany;
      const response = await api.get('question-bank/', { params });
      const results = Array.isArray(response.data) ? response.data : (response.data.results || []);
      const count = response.data?.count ?? results.length;
      setQuestions(results);
      setPagination({
        current_page: page,
        total_pages: Math.max(1, Math.ceil(count / pageSize)),
        total_count: count,
      });
    } catch (error) {
      setQuestions([]);
      showSnackbar(t('questionBank.loadFailed', 'Failed to load Question Bank.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, questionType, selectedCompany, t]);

  useEffect(() => {
    if (!isSuperAdmin && !isCompanyAdmin) {
      navigate('/dashboard');
    }
  }, [isSuperAdmin, isCompanyAdmin, navigate]);

  useEffect(() => {
    api.get('companies/')
      .then((res) => {
        const companiesData = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setCompanies(companiesData);
        if (!isSuperAdmin && userDetails.admin_companies?.length === 1) {
          const match = companiesData.find((company) => userDetails.admin_companies.includes(company.name));
          if (match) setSelectedCompany(String(match.id));
        }
      })
      .catch(() => setCompanies([]));
  }, [isSuperAdmin, userDetails.admin_companies]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const openCreate = () => {
    setEditingQuestion(null);
    setForm(emptyQuestionForm());
    setFormOpen(true);
  };

  const openEdit = (question) => {
    setEditingQuestion(question);
    setForm(formFromBankItem(question));
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!isQuestionFormValid(form)) return;
    if (isSuperAdmin && !selectedCompany && !editingQuestion) {
      showSnackbar(t('questionBank.companyRequired', 'Select a company before adding a question.'), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (selectedCompany) payload.company = Number(selectedCompany);
      if (editingQuestion) {
        await api.patch(`question-bank/${editingQuestion.id}/`, payload);
        showSnackbar(t('questionBank.updated', 'Question updated.'));
      } else {
        await api.post('question-bank/', payload);
        showSnackbar(t('questionBank.created', 'Question added to the Question Bank.'));
      }
      setFormOpen(false);
      fetchQuestions();
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.choices || t('questionBank.saveFailed', 'Failed to save question.');
      showSnackbar(typeof detail === 'string' ? detail : t('questionBank.saveFailed', 'Failed to save question.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = async (question) => {
    try {
      const response = await api.get(`question-bank/${question.id}/usage/`);
      setDeleteTarget({
        ...question,
        used_in_count: response.data?.used_in_count || 0,
        job_descriptions: response.data?.job_descriptions || [],
      });
    } catch (error) {
      setDeleteTarget({ ...question, used_in_count: question.used_in_count || 0, job_descriptions: [] });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const used = (deleteTarget.used_in_count || 0) > 0;
      await api.delete(`question-bank/${deleteTarget.id}/`, {
        params: used ? { force: true } : undefined,
      });
      showSnackbar(t('questionBank.deleted', 'Question deleted.'));
      setDeleteTarget(null);
      fetchQuestions();
    } catch (error) {
      if (error.response?.status === 409) {
        setDeleteTarget({
          ...deleteTarget,
          used_in_count: error.response.data.used_in_count,
          job_descriptions: error.response.data.job_descriptions || [],
        });
      } else {
        showSnackbar(t('questionBank.deleteFailed', 'Failed to delete question.'), 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  const openUsage = async (question) => {
    setUsageQuestion(question);
    setUsageLoading(true);
    try {
      const response = await api.get(`question-bank/${question.id}/usage/`);
      setUsageList(response.data?.job_descriptions || []);
    } catch (error) {
      setUsageList([]);
    } finally {
      setUsageLoading(false);
    }
  };

  const closeUsage = () => {
    setUsageQuestion(null);
    setUsageList([]);
  };

  const deleteMessage = deleteTarget?.used_in_count > 0
    ? t(
      'questionBank.deleteInUseMessage',
      'This question is currently being used in {{count}} JDs/interviews. Deleting it will also remove it from those JDs. Are you sure you want to continue?',
      { count: deleteTarget.used_in_count }
    )
    : t('questionBank.deleteConfirm', 'Are you sure you want to delete this question?');

  const usageCount = usageLoading
    ? (Number(usageQuestion?.used_in_count) || 0)
    : usageList.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30" style={{ paddingTop: '10px' }}>
      <NotificationPopup
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />

      <div className="w-full max-w-[1920px] mx-auto px-4 py-3 sm:px-6 lg:px-8 h-full">
        <div className="bg-white rounded-xl flex flex-col h-full overflow-hidden">
          <div className="px-4 py-3 text-white rounded-t-xl shadow-sm" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h1 className="text-lg font-semibold mb-0.5 text-white">{t('questionBank.title', 'Question Bank')}</h1>
                <p className="text-xs text-blue-100">{t('questionBank.subtitle', 'Create and reuse interview questions for your company')}</p>
              </div>
              <button
                onClick={openCreate}
                className="flex items-center gap-1.5 bg-white text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors shadow-sm text-sm font-medium"
              >
                <Plus size={14} />
                {t('questionBank.addQuestion', 'Add Question')}
              </button>
            </div>
          </div>

          <div className="p-4 border-b border-gray-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder={t('questionBank.searchPlaceholder', 'Search questions...')}
                value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              />
            </div>
            <select
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={questionType}
              onChange={(e) => { setPage(1); setQuestionType(e.target.value); }}
            >
              <option value="">{t('questionBank.allTypes', 'All types')}</option>
              {questionTypeOptions.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            {companies.length > 1 && (
              <select
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={selectedCompany}
                onChange={(e) => { setPage(1); setSelectedCompany(e.target.value); }}
              >
                <option value="">{t('jobDescription.allCompanies', 'All companies')}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <CircularProgress size={48} sx={{ color: '#0ea5e9' }} />
                <p className="mt-4 text-gray-600 font-medium">{t('questionBank.loading', 'Loading questions...')}</p>
              </div>
            ) : questions.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                {t('questionBank.empty', 'No questions in the Question Bank yet.')}
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-1 gap-4">
                    {questions.map((question) => {
                      const mcqChoices = question.question_type === 'mcq' ? getMcqChoices(question) : [];
                      const usedCount = Number(question.used_in_count) || 0;
                      const isUsed = usedCount > 0;
                      return (
                      <article
                        key={question.id}
                        className="min-w-0 h-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-sky-200 transition-all flex flex-col"
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex flex-wrap items-center gap-2 min-w-0">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getQuestionTypeColor(question.question_type)}`}>
                              {questionTypeOptions.find((opt) => opt.value === question.question_type)?.label || question.question_type}
                            </span>
                            {question.question_type === 'coding' && (
                              <span className="text-xs text-gray-500">{question.time_limit || 5} min</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => openEdit(question)}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                              title={t('common.edit', 'Edit')}
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => requestDelete(question)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                              title={t('common.delete', 'Delete')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>

                        <div className="text-sm text-gray-800 whitespace-pre-wrap font-medium break-words">
                          {question.text}
                        </div>

                        {question.question_type === 'mcq' && (
                          <div className="mt-3 space-y-1.5">
                            {mcqChoices.length === 0 ? (
                              <p className="text-xs text-gray-400 italic">
                                {t('questionBank.noMcqOptions', 'No multiple choice options saved for this question.')}
                              </p>
                            ) : (
                              mcqChoices.map((choice, cIdx) => (
                                <div
                                  key={`${question.id}-choice-${cIdx}`}
                                  className={`flex items-start gap-2 text-sm rounded-lg px-2.5 py-1.5 ${
                                    choice.is_correct
                                      ? 'bg-green-50 border border-green-200'
                                      : 'bg-gray-50 border border-gray-100'
                                  }`}
                                >
                                  <span className="text-gray-500 font-semibold w-5 shrink-0">
                                    {String.fromCharCode(65 + cIdx)}.
                                  </span>
                                  <span className={`flex-1 min-w-0 break-words ${choice.is_correct ? 'text-green-800 font-medium' : 'text-gray-700'}`}>
                                    {choice.text}
                                  </span>
                                  {choice.is_correct && (
                                    <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold shrink-0">
                                      {t('questionBank.correct', 'Correct')}
                                    </span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}

                        <div className="mt-auto pt-3 border-t border-gray-100">
                          <button
                            type="button"
                            onClick={() => openUsage(question)}
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                              isUsed
                                ? 'bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-100 hover:border-sky-300 hover:shadow-sm'
                                : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                            }`}
                            title={isUsed
                              ? t('questionBank.viewUsage', 'View linked job descriptions')
                              : t('questionBank.notUsed', 'This question is not used in any JDs.')}
                          >
                            <Briefcase size={13} className={isUsed ? 'text-sky-600' : 'text-gray-400'} />
                            {isUsed ? (
                              <>
                                <span>{t('questionBank.usedInLabel', 'Used in')}</span>
                                <span className="inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-sky-600 px-1.5 text-[11px] font-bold text-white">
                                  {usedCount}
                                </span>
                                <span>
                                  {usedCount === 1
                                    ? t('questionBank.jdSingular', 'JD')
                                    : t('questionBank.jdPlural', 'JDs')}
                                </span>
                              </>
                            ) : (
                              <span>{t('questionBank.notUsedShort', 'Not used in any JDs')}</span>
                            )}
                          </button>
                        </div>
                      </article>
                      );
                    })}
                </div>
                <div className="mt-4">
                  <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    totalCount={pagination.total_count}
                    currentCount={questions.length}
                    onPageChange={setPage}
                    pageSize={pageSize}
                    forceVisible
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <QuestionBankFormModal
        open={formOpen}
        editingQuestion={editingQuestion}
        form={form}
        setForm={setForm}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      {usageQuestion && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[999999]"
          onClick={closeUsage}
        >
          <div
            className="bg-white rounded-[20px] w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            style={{ boxShadow: '0 25px 50px -12px rgba(14, 165, 233, 0.25)' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="px-6 py-5 text-white relative shrink-0"
              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
            >
              <div className="flex items-start gap-3 pr-10">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)' }}
                >
                  <Briefcase size={20} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-white leading-tight">
                    {t('questionBank.usedIn', 'Used In')}
                  </h3>
                  <p className="text-xs text-white/90 mt-0.5">
                    {t('questionBank.usedInSubtitle', 'Job descriptions linked to this question')}
                  </p>
                  <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/20 border border-white/35 px-2.5 py-1 text-xs font-semibold text-white">
                    <span className="inline-flex min-w-[1.15rem] h-[18px] items-center justify-center rounded-full bg-white text-sky-600 px-1 text-[11px] font-bold">
                      {usageCount}
                    </span>
                    <span>
                      {usageCount === 1
                        ? t('questionBank.jdSingular', 'JD')
                        : t('questionBank.jdPlural', 'JDs')}
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={closeUsage}
                className="absolute top-3 right-3 p-1.5 rounded-lg text-white hover:bg-white/20 transition-colors"
                aria-label={t('common.close', 'Close')}
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              {usageQuestion.text && (
                <div className="mb-4 rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-2.5">
                  <p className="text-sm text-gray-700 font-medium line-clamp-3 whitespace-pre-wrap">
                    {usageQuestion.text}
                  </p>
                </div>
              )}

              {usageLoading ? (
                <div className="py-10 flex flex-col items-center justify-center gap-3">
                  <CircularProgress size={28} sx={{ color: '#0ea5e9' }} />
                  <p className="text-sm text-gray-500">{t('questionBank.loadingUsage', 'Loading linked JDs...')}</p>
                </div>
              ) : usageList.length === 0 ? (
                <div className="py-10 text-center">
                  <div className="mx-auto w-14 h-14 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center mb-3">
                    <Briefcase size={22} className="text-gray-400" />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">
                    {t('questionBank.notUsedShort', 'Not used in any JDs')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                    {t('questionBank.emptyUsageHint', 'This question is not linked to any job descriptions yet.')}
                  </p>
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {usageList.map((jd) => (
                    <li key={jd.id}>
                      <button
                        type="button"
                        className="group w-full text-left flex items-center gap-3 p-3.5 rounded-2xl border border-gray-200 hover:border-sky-300 hover:bg-sky-50/70 hover:shadow-sm transition-all"
                        onClick={() => navigate('/jobdescriptions')}
                      >
                        <div className="w-10 h-10 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600 shrink-0 group-hover:bg-sky-100">
                          <Briefcase size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{jd.title}</div>
                          <span className={`mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${getJdStatusStyle(jd.status)}`}>
                            {t(`jobDescription.${jd.status}`, jd.status)}
                          </span>
                        </div>
                        <ChevronRight size={18} className="text-gray-300 group-hover:text-sky-500 shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div
              className="px-6 py-4 flex justify-end border-t border-slate-100 shrink-0"
              style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}
            >
              <button
                type="button"
                onClick={closeUsage}
                className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-all"
              >
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={t('questionBank.deleteTitle', 'Delete Question')}
        message={deleteMessage}
        confirmText={deleteTarget?.used_in_count > 0
          ? t('questionBank.deleteAnyway', 'Delete Anyway')
          : t('common.delete', 'Delete')}
      />
    </div>
  );
}
